import { useMemo, useState } from "react"
import { CalendarIcon } from "lucide-react"
import { ru } from "react-day-picker/locale"

import {
  formatRentDateShort,
  formatRentWeekdayLabel,
  getMaxRentDate,
  getNextRentDates,
  getTodayRentDate,
  parseRentDateString,
  toRentDateString,
} from "@/components/rent/rent-constants"
import { Button } from "@/components/ui-kit/button"
import { Calendar } from "@/components/ui/calendar"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { cn } from "@/lib/utils"

type RentDatePickerSectionProps = {
  value: string
  onValueChange: (value: string) => void
  disabled?: boolean
}

export function RentDatePickerSection({
  value,
  onValueChange,
  disabled = false,
}: RentDatePickerSectionProps) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const dates = getNextRentDates(8)
  const minDate = useMemo(() => getTodayRentDate(), [])
  const maxDate = useMemo(() => getMaxRentDate(), [])
  const selectedDate = value ? parseRentDateString(value) : undefined

  const handleCalendarSelect = (date: Date | undefined) => {
    if (!date) {
      return
    }

    onValueChange(toRentDateString(date))
    setIsCalendarOpen(false)
  }

  return (
    <section className="flex min-w-0 flex-col gap-4 overflow-x-hidden">
      <div className="flex gap-4 lg:flex-row flex-col items-start lg:items-center">
        <div className="min-w-0 flex-1">
          <h2 className="text-[18px] leading-[24px] font-medium text-foreground">
            Когда хотите начать аренду?
          </h2>
          <p className="mt-1 text-[12px] leading-[16px] text-light-muted">
            Выберите дату начала аренды. Подписка на бокс начинается с этой даты
          </p>
        </div>

        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-12 shrink-0 rounded-lg text-brand hover:text-brand/80 hover:bg-transparent hidden lg:block"
              disabled={disabled}
              aria-label="Открыть календарь"
            >
              <CalendarIcon className="size-6" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0">
            <Calendar
              mode="single"
              locale={ru}
              selected={selectedDate}
              onSelect={handleCalendarSelect}
              defaultMonth={selectedDate ?? minDate}
              disabled={{ before: minDate, after: maxDate }}
            />
          </PopoverContent>
        </Popover>
      </div>

      <RadioGroup
        value={value}
        onValueChange={onValueChange}
        className={cn(
          "flex gap-1 pb-1",
          " overflow-x-auto ",
          "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          "xl:mx-0 xl:w-full xl:overflow-visible xl:px-0 xl:pb-0",
        )}
        disabled={disabled}
      >
        {dates.map((date) => {
          const itemId = `rent-date-${date}`

          return (
            <div key={date} className="shrink-0 xl:min-w-0 xl:flex-1">
              <RadioGroupItem id={itemId} value={date} className="peer sr-only hidden" />
              <Label
                htmlFor={itemId}
                className={cn(
                  "flex min-h-[80px] w-[105px] cursor-pointer flex-col items-center justify-center gap-1 rounded-[4px] border-2 border-transparent bg-background px-2 py-3 xl:w-full",
                  "hover:border-brand hover:bg-card",
                  "peer-data-[state=checked]:border-brand peer-data-[state=checked]:bg-card peer-data-[state=checked]:pointer-events-none",
                )}
              >
                <span className="text-[12px] leading-4 text-light-muted">
                  {formatRentWeekdayLabel(date)}
                </span>
                <span className="text-[18px] leading-6 font-medium text-foreground">
                  {formatRentDateShort(date)}
                </span>
              </Label>
            </div>
          )
        })}
      </RadioGroup>
    </section>
  )
}
